<script lang="ts" setup>
import type {FreeResource} from '~/types/legacy'
import type {Populated} from '~/types/populated'
import {resolveMediaProps} from '~/utils/relations'

const props = withDefaults(
  defineProps<{
    resource: Populated<FreeResource>
    direction?: 'row' | 'col'
  }>(),
  {
    direction: 'row',
  }
)

const {formatDate} = useDate()

const isRowMode = computed(() => props.direction === 'row')
</script>

<template>
  <div
    :class="[
      'flex overflow-hidden bg-white transition-all hover:opacity-90',
      'h-auto w-full flex-col',
      isRowMode ? 'mt-8 md:h-[280px] md:max-w-[800px] md:flex-row' : 'lg:h-full lg:w-[320px] lg:flex-col',
    ]"
  >
    <div :class="['aspect-[6/4] flex-shrink-0 overflow-hidden', isRowMode && 'md:rounded-l-lg']">
      <NuxtImg
        :class="['rounded-lg object-cover transition-transform duration-300 hover:scale-105', 'h-full w-full']"
        v-bind="resolveMediaProps(props.resource.thumbnail)"
        fit="cover"
      />
    </div>

    <div :class="['flex min-h-[180px] flex-col px-6 md:px-6']">
      <div class="grid flex-1 grid-rows-[auto_auto_1fr] gap-2">
        <div :class="['my-3 flex items-center gap-2 text-sm', 'flex-wrap sm:flex-nowrap']">
          <span class="min-w-0 truncate font-medium text-main-700">
            {{ props.resource.category?.name }}
          </span>
          <span class="hidden text-gray-400 sm:inline">-</span>
          <span :class="['flex-shrink-0 text-gray-500', 'sm:flex-shrink']">
            {{ formatDate(new Date(props.resource.createdAt)) }}
          </span>
        </div>

        <div class="flex min-h-[3rem] items-start sm:min-h-[3.5rem] md:min-h-[4rem]">
          <Heading
            as="h3"
            :class="[
              'line-clamp-2 font-bold leading-tight',
              'mb-2',
              isRowMode ? 'text-lg sm:text-xl md:text-3xl' : 'text-lg sm:text-xl',
            ]"
          >
            {{ resource.title }}
          </Heading>
        </div>

        <div class="flex items-start">
          <Paragraph size="small" :class="['line-clamp-3 leading-relaxed text-gray-600', 'text-lg']">
            {{ resource.description }}
          </Paragraph>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@media (max-width: 767px) {
  .line-clamp-3 {
    -webkit-line-clamp: 2;
  }
}

@media (hover: hover) {
  .hover\:scale-105:hover {
    transform: scale(1.05);
  }

  .hover\:opacity-90:hover {
    opacity: 0.9;
  }
}
</style>
